/**
 * BrandingContext — fetches runtime branding from the backend
 * (GET /api/public/branding) on app startup and provides the
 * resolved values to every component.
 *
 * Uses localStorage so refresh shows the last-known academy name/logo
 * immediately instead of deploy-time defaults from appConfig.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import appConfig from '../config/appConfig';
import { getPublicBranding, brandingLogoUrl } from '../api/branding';
import { readBrandingCache, writeBrandingCache } from '../utils/brandingCache';

const BrandingContext = createContext(null);

/** Point the browser-tab favicon at the academy's logo (falls back to default). */
function setFavicon(href) {
    if (!href || typeof document === 'undefined') return;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
    }
    if (link.getAttribute('href') !== href) {
        link.setAttribute('href', href);
    }
}

function resolveLogoUrl(hasLogo, logoVersion) {
    if (!hasLogo) return appConfig.logoPath;
    return logoVersion
        ? `${brandingLogoUrl()}?v=${encodeURIComponent(logoVersion)}`
        : brandingLogoUrl();
}

function resolveFromApi(data, { bustLogo = false, previousCache = null } = {}) {
    const hasLogo = !!data.hasLogo;
    let logoVersion = previousCache?.logoVersion || '1';
    if (bustLogo || (hasLogo && !previousCache?.logoVersion)) {
        logoVersion = String(Date.now());
    }

    return {
        appName: (data.appName && data.appName.trim()) || appConfig.appName,
        tagline: (data.tagline && data.tagline.trim()) || appConfig.tagline,
        hasLogo,
        logoUrl: resolveLogoUrl(hasLogo, hasLogo ? logoVersion : null),
        supportEmail: data.supportEmail || '',
        supportPhone: data.supportPhone || '',
        logoVersion: hasLogo ? logoVersion : null,
    };
}

function getInitialBranding() {
    const cached = readBrandingCache();
    if (cached?.appName) {
        if (typeof document !== 'undefined') {
            document.title = cached.appName;
        }
        return {
            ...cached,
            ready: false,
            hydrated: true,
        };
    }
    return {
        appName: '',
        tagline: '',
        logoUrl: null,
        hasLogo: false,
        supportEmail: '',
        supportPhone: '',
        logoVersion: null,
        ready: false,
        hydrated: false,
    };
}

export function BrandingProvider({ children }) {
    const [branding, setBranding] = useState(getInitialBranding);

    const load = useCallback(async (options = {}) => {
        const previousCache = readBrandingCache();
        try {
            const data = await getPublicBranding();
            const resolved = resolveFromApi(data, {
                bustLogo: options.bustLogo,
                previousCache,
            });
            writeBrandingCache(resolved);
            setBranding({ ...resolved, ready: true, hydrated: true });
            if (resolved.appName && typeof document !== 'undefined') {
                document.title = resolved.appName;
            }
        } catch {
            if (previousCache?.appName) {
                setBranding(prev => ({
                    ...prev,
                    ...previousCache,
                    ready: true,
                    hydrated: true,
                }));
            } else {
                setBranding({
                    appName: appConfig.appName,
                    tagline: appConfig.tagline,
                    logoUrl: appConfig.logoPath,
                    hasLogo: false,
                    supportEmail: '',
                    supportPhone: '',
                    logoVersion: null,
                    ready: true,
                    hydrated: true,
                });
            }
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // Keep the browser-tab favicon in sync with the academy logo.
    useEffect(() => {
        setFavicon(branding.logoUrl || appConfig.logoPath);
    }, [branding.logoUrl]);

    return (
        <BrandingContext.Provider value={{ ...branding, refresh: load }}>
            {children}
        </BrandingContext.Provider>
    );
}

/** Hook for consuming branding in any component */
export function useBranding() {
    const ctx = useContext(BrandingContext);
    if (!ctx) throw new Error('useBranding must be used inside <BrandingProvider>');
    return ctx;
}
